import { Box, Button, Group, SegmentedControl } from "@mantine/core"
import { useDebouncedValue } from "@mantine/hooks"
import React from "react"
import { useTranslation } from "react-i18next"
import { HelpIcon } from "@dehub/theme"
import { useNavigate } from "react-router"
import useSWR from "swr"
import { SavedListApi } from "../../apis"
import { toCartItem, useAddLinesToCart } from "../../components/buyer-sales/quick-order/cart-lines"
import { buildDisplayRows, isAddable } from "../../components/buyer-sales/quick-order/match-rows"
import { MatchTable } from "../../components/buyer-sales/quick-order/MatchTable"
import { MAX_MATCH_ROWS, parseLines, sameCode, serializeRows } from "../../components/buyer-sales/quick-order/parse"
import classes from "../../components/buyer-sales/quick-order/quick-order.module.css"
import { QuickOrderInput, type QuickOrderMode } from "../../components/buyer-sales/quick-order/QuickOrderInput"
import { useBuyerSuppliers, useSupplierChoice } from "../../components/buyer-sales/quick-order/use-buyer-supplier"
import { useCatalogue, flattenCatalogue } from "../../components/buyer-sales/product-list/catalogue-data"
import { SupplierSelect } from "../../components/buyer-sales/supplier-select"
import { TableFrame } from "../../components/delivery/atoms"
import { PageLayout } from "../../components/layout"
import { usePermission } from "../../components/permission"
import { DeniedCard, ListState } from "../../components/ui"

/** «Олноор нэмэх» (B25, canvas OrdB_QuickOrder): SKU lines, an Excel file or scans, matched against one supplier. */
export function BuyerSalesQuickOrder() {
  const { t } = useTranslation(["bulkOrder", "order", "common", "supplierSales"])
  const navigate = useNavigate()
  const ORD_LIST = usePermission("ORD_LIST")
  const { suppliers, isLoading } = useBuyerSuppliers(ORD_LIST.hasAccess)
  const { supplierId, supplier, setSupplierId } = useSupplierChoice(suppliers)

  const [mode, setMode] = React.useState<QuickOrderMode>("sku")
  const [text, setText] = React.useState("")
  const [debouncedText] = useDebouncedValue(text, 400)

  const inputRows = React.useMemo(() => parseLines(debouncedText), [debouncedText])
  const requestRows = React.useMemo(
    () => inputRows.filter((r) => !r.invalid).slice(0, MAX_MATCH_ROWS).map((r) => ({ code: r.code, quantity: r.quantity })),
    [inputRows],
  )

  const { data: matched, error, isValidating: matching, mutate } = useSWR(
    supplierId && requestRows.length > 0 ? ["quick-order-match", supplierId, JSON.stringify(requestRows)] : null,
    () => SavedListApi.match({ supplierBusinessId: supplierId, rows: requestRows }),
    { revalidateOnFocus: false },
  )

  const rows = React.useMemo(() => buildDisplayRows(inputRows, requestRows.length > 0 ? matched || [] : []), [inputRows, requestRows, matched])
  // The result on screen must belong to the text on screen: while typing settles or
  // the request for this text is in flight, show loading and keep «Сагсанд нэмэх» off.
  const pending = text !== debouncedText || (requestRows.length > 0 && (matching || !matched))
  const tableState =
    parseLines(text).length === 0 ? "empty" : pending && !error ? "loading" : error ? "error" : "data"

  const catalogueSuppliers = React.useMemo(() => (supplier ? [supplier] : []), [supplier])
  // Cart lines take their warehouse from the catalogue, so adding waits for it.
  const { data: catalogue } = useCatalogue(catalogueSuppliers)
  const addLines = useAddLinesToCart()

  const handleRemoveBad = () => {
    const badCodes = rows.filter((r) => r.status === "NOT_FOUND").map((r) => r.code)
    const kept = parseLines(text).filter((r) => !r.invalid && !badCodes.some((c) => sameCode(c, r.code)))
    setText(serializeRows(kept))
  }

  const handleAdd = () => {
    if (pending || !catalogue) return
    const products = flattenCatalogue(catalogue)
    const missing: string[] = []
    const entries = rows.filter(isAddable).flatMap((r) => {
      const product = products.find((p) => p.id === r.variantId)
      // Not in the catalogue = no warehouse; order create would refuse the line.
      if (!product) {
        missing.push(r.skuCode || r.code)
        return []
      }
      const item = toCartItem(
        { variantId: r.variantId!, parentVariantId: r.parentVariantId, quantity: r.addQty, price: r.price, name: r.name, skuCode: r.skuCode, image: r.image },
        supplier,
        product,
      )
      const max = r.status === "LOW_STOCK" ? (r.available ?? r.addQty) : r.available
      return [{ item, max }]
    })
    addLines(entries, missing)
  }

  const breadcrumb = [
    { to: "/", label: t("common:breadcrumb.home") },
    { to: "/buyer-sales", label: t("order:buyerSales.breadcrumb") },
    { to: "/buyer-sales/new", label: t("order:catalog.title") },
    { label: t("bulkOrder:quick.title") },
  ]

  if (!ORD_LIST.hasAccess) {
    return (
      <PageLayout title={t("bulkOrder:quick.title")} breadcrumb={breadcrumb} noFrame>
        <DeniedCard permission="ORD_LIST" />
      </PageLayout>
    )
  }

  if (isLoading || suppliers.length === 0) {
    return (
      <PageLayout title={t("bulkOrder:quick.title")} breadcrumb={breadcrumb} noFrame>
        <TableFrame>
          {isLoading ? (
            <ListState state="loading" />
          ) : (
            <ListState
              state="empty"
              title={t("supplierSales:customer.noSupplier")}
              description={t("supplierSales:customer.noSupplierDescription")}
              action={{ label: t("supplierSales:customer.backToList"), onClick: () => navigate("/buyer-sales") }}
            />
          )}
        </TableFrame>
      </PageLayout>
    )
  }

  const title = (
    <Box component="span" className={classes.titleRow}>
      <Box component="span">{t("bulkOrder:quick.title")}</Box>
      {suppliers.length > 1 && <SupplierSelect suppliers={suppliers} value={supplierId} onChange={setSupplierId} />}
    </Box>
  )

  const extra = [
    <Button key="saved" variant="default" size="md" onClick={() => navigate(`/buyer-sales/saved-lists?supplier=${supplierId}`)}>
      {t("bulkOrder:saved.title")}
    </Button>,
    <Group key="mode" gap="xs" wrap="nowrap">
      <SegmentedControl
        value={mode}
        onChange={(v) => setMode(v as QuickOrderMode)}
        radius="md"
        size="md"
        data={[
          { value: "sku", label: t("bulkOrder:quick.modeSku") },
          { value: "excel", label: t("bulkOrder:quick.modeExcel") },
          { value: "barcode", label: t("bulkOrder:quick.modeBarcode") },
        ]}
      />
      <HelpIcon helpKey="order.buyer-sales-quick-order.types.mode" />
    </Group>,
  ]

  return (
    <PageLayout title={title} breadcrumb={breadcrumb} extra={extra} noFrame>
      <Box className={classes.layout}>
        <QuickOrderInput mode={mode} text={text} onTextChange={setText} />
        <MatchTable
          rows={rows}
          state={tableState}
          onRetry={() => mutate()}
          onRemoveBad={handleRemoveBad}
          onAdd={handleAdd}
          addDisabled={pending || !catalogue}
        />
      </Box>
    </PageLayout>
  )
}
